import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';

interface ResizableSplitProps {
  top: ReactNode;
  bottom: ReactNode;
  /** localStorage key the split position (top panel %) is persisted under. */
  storageKey: string;
  /** Default top-panel size, as a percentage of the container height. */
  defaultTopPercent?: number;
  /** Top-panel size used by the double-click "expand response" toggle. */
  expandedTopPercent?: number;
  /** Minimum size of *either* panel, in pixels — the only hard floor; there is no percentage ceiling. */
  minPx?: number;
  /**
   * Optional maximum size of *either* panel, as a percentage of the
   * container height. Omit to allow the divider to travel freely between
   * the two panels' `minPx` floors — e.g. the Request/Response split
   * deliberately has none, so either panel can be dragged down to a sliver
   * while the other takes almost the whole viewport.
   */
  maxPercent?: number;
  /** ArrowUp/ArrowDown step, in percentage points. */
  stepPercent?: number;
  /** Shift+ArrowUp/ArrowDown step, in percentage points. */
  bigStepPercent?: number;
  /** Hides the bottom panel entirely; top takes the full height, no divider rendered. */
  bottomHidden?: boolean;
  /** Hides the top panel entirely; bottom takes the full height, no divider rendered. */
  topHidden?: boolean;
}

/**
 * Clamps the top panel's size so that BOTH panels simultaneously respect the
 * same minPx floor — not just the top one. Given a container height, the
 * top panel's size (as a %) is bounded below by its own min-px-as-a-percent
 * and (when `maxPercent` is given) by the bottom panel's max-percent
 * (`100 - maxPercent`); bounded above by `maxPercent` (if given) and by the
 * bottom panel's min-px-as-a-percent. With no `maxPercent`, the only limits
 * are the two panels' pixel floors, so the split can travel almost the full
 * range of the container.
 */
const clampTopPercent = (
  value: number,
  containerHeight: number,
  minPx: number,
  maxPercent: number | undefined,
): number => {
  if (containerHeight <= 0) return value;

  const minPercentFromPx = (minPx / containerHeight) * 100;
  const low =
    maxPercent === undefined ? minPercentFromPx : Math.max(minPercentFromPx, 100 - maxPercent);
  const high =
    maxPercent === undefined
      ? 100 - minPercentFromPx
      : Math.min(maxPercent, 100 - minPercentFromPx);

  if (low > high) {
    // Container too short to satisfy both panels' minimums at once — split evenly rather than error.
    return 50;
  }

  return Math.min(high, Math.max(low, value));
};

const readStoredPercent = (storageKey: string, fallback: number): number => {
  if (typeof window === 'undefined') return fallback;

  try {
    const stored = window.localStorage.getItem(storageKey);
    const parsed = stored === null ? NaN : Number(stored);
    return Number.isFinite(parsed) ? parsed : fallback;
  } catch {
    return fallback;
  }
};

/**
 * A vertically-stacked, mouse/touch/keyboard-resizable two-pane split (used
 * for Request Builder / Response Viewer). Deliberately hand-rolled instead
 * of pulling in a resizable-panel library: the exact pixel-min +
 * percent-max hybrid constraint, the double-click expand/collapse preset,
 * and the specific localStorage key are all easier to guarantee precisely
 * this way than by bending a third-party API to fit.
 */
export const ResizableSplit = ({
  top,
  bottom,
  storageKey,
  defaultTopPercent = 55,
  expandedTopPercent = 25,
  minPx = 200,
  maxPercent,
  stepPercent = 2,
  bigStepPercent = 10,
  bottomHidden = false,
  topHidden = false,
}: ResizableSplitProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const dragState = useRef<{ startY: number; startTopPercent: number } | null>(null);

  const [containerHeight, setContainerHeight] = useState(0);
  const [topPercent, setTopPercent] = useState(() =>
    readStoredPercent(storageKey, defaultTopPercent),
  );
  const [isExpanded, setIsExpanded] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    setContainerHeight(el.getBoundingClientRect().height);

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) setContainerHeight(entry.contentRect.height);
    });
    observer.observe(el);

    return () => observer.disconnect();
  }, []);

  const clamp = useCallback(
    (value: number) => clampTopPercent(value, containerHeight, minPx, maxPercent),
    [containerHeight, minPx, maxPercent],
  );

  const persist = useCallback(
    (value: number) => {
      try {
        window.localStorage.setItem(storageKey, String(value));
      } catch {
        // Private browsing / quota exceeded — resizing still works, it just won't persist.
      }
    },
    [storageKey],
  );

  const handlePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if (event.button !== undefined && event.button !== 0) return;
    event.currentTarget.setPointerCapture(event.pointerId);
    dragState.current = { startY: event.clientY, startTopPercent: topPercent };
    setIsDragging(true);
  };

  const handlePointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!dragState.current) return;
    const deltaY = event.clientY - dragState.current.startY;
    const deltaPercent = containerHeight > 0 ? (deltaY / containerHeight) * 100 : 0;
    setTopPercent(clamp(dragState.current.startTopPercent + deltaPercent));
  };

  const endDrag = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!dragState.current) return;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    dragState.current = null;
    setIsDragging(false);
    setTopPercent((current) => {
      persist(current);
      return current;
    });
  };

  const handleDoubleClick = () => {
    const next = clamp(isExpanded ? defaultTopPercent : expandedTopPercent);
    setIsExpanded((value) => !value);
    setTopPercent(next);
    persist(next);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    let delta = 0;
    if (event.key === 'ArrowUp') delta = event.shiftKey ? -bigStepPercent : -stepPercent;
    else if (event.key === 'ArrowDown') delta = event.shiftKey ? bigStepPercent : stepPercent;
    else return;

    event.preventDefault();
    setTopPercent((current) => {
      const next = clamp(current + delta);
      persist(next);
      return next;
    });
  };

  // While dragging: force the resize cursor everywhere and block text
  // selection, so a fast pointer that briefly leaves the handle doesn't
  // break the drag or select the page's text.
  useEffect(() => {
    if (!isDragging) return;

    const previousUserSelect = document.body.style.userSelect;
    const previousCursor = document.body.style.cursor;
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'row-resize';

    return () => {
      document.body.style.userSelect = previousUserSelect;
      document.body.style.cursor = previousCursor;
    };
  }, [isDragging]);

  const clampedTop = clamp(topPercent);
  const minPercentFromPx = containerHeight > 0 ? (minPx / containerHeight) * 100 : 0;
  const ariaMin = Math.max(minPercentFromPx, 100 - (maxPercent ?? 100));
  const ariaMax = Math.min(maxPercent ?? 100, 100 - minPercentFromPx);

  const showSplit = !bottomHidden && !topHidden;

  // Both `top` and `bottom` are ALWAYS rendered (never conditionally
  // included/excluded from the JSX) — only their CSS visibility toggles.
  // Swapping between different element shapes at this position (e.g. a
  // single wrapper div vs. a three-child Fragment) makes React treat it as
  // a different subtree and remount everything inside, which would silently
  // reset any state living inside `top`/`bottom` (an uncontrolled antd
  // Tabs' selected key, Monaco's cursor/scroll position, this component's
  // own ResizeObserver-holding container, etc.) every time hide/fullscreen
  // is toggled.
  return (
    <div ref={containerRef} className="flex h-full min-h-0 w-full flex-1 flex-col overflow-hidden">
      <div
        style={{
          height: showSplit ? `${clampedTop}%` : undefined,
          display: topHidden ? 'none' : undefined,
        }}
        className={`min-h-0 w-full overflow-hidden ${showSplit ? '' : 'flex-1'}`}
      >
        {top}
      </div>

      {showSplit && (
        <div
          role="separator"
          aria-orientation="horizontal"
          aria-label="Resize request and response panels"
          aria-valuenow={Math.round(clampedTop)}
          aria-valuemin={Math.round(ariaMin)}
          aria-valuemax={Math.round(ariaMax)}
          tabIndex={0}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={endDrag}
          onPointerCancel={endDrag}
          onDoubleClick={handleDoubleClick}
          onKeyDown={handleKeyDown}
          style={{ touchAction: 'none' }}
          className={`group relative flex h-2.5 w-full shrink-0 cursor-row-resize select-none items-center justify-center border-y border-black/10 bg-black/[0.02] outline-none transition-colors hover:bg-blue-500/10 focus-visible:bg-blue-500/15 dark:border-white/10 dark:bg-white/[0.02] dark:hover:bg-blue-400/10 dark:focus-visible:bg-blue-400/15 ${
            isDragging ? 'bg-blue-500/15 dark:bg-blue-400/15' : ''
          }`}
        >
          <div
            className={`h-1 w-9 rounded-full bg-black/20 transition-colors group-hover:bg-blue-500/70 dark:bg-white/20 dark:group-hover:bg-blue-400/70 ${
              isDragging ? 'bg-blue-500/70 dark:bg-blue-400/70' : ''
            }`}
          />
        </div>
      )}

      <div
        style={{ display: bottomHidden ? 'none' : undefined }}
        className="min-h-0 w-full flex-1 overflow-hidden"
      >
        {bottom}
      </div>
    </div>
  );
};
